import PropTypes from 'prop-types';

import { Box, IconButton, TableCell, TableRow, TextField } from '@mui/material';

import {
  CheckOutlined,
  ClearOutlined, EditOutlined
} from '@mui/icons-material';

import {
  addInputCellSx,
  addActionCellSx,
  actionWrapperSx,
  inputSx,
  actionIconSx, clearIconSx,
  editIconButtonSx,
  actionSvgSx,
  editSvgSx,
  clickableTableRowSx,
  editingTableRowSx,
  clickableTopicTextSx,
  addIconSx,
  iconButtonNoAnimationSx
} from '../Styles/InputStyles';

const TemplateRow = ({
  topic,
  selected,
  editing,
  editingValue,
  onEditingValueChange,
  onSelect,
  onEdit,
  onSave,
  onCancel
}) => {
  const handleRowClick = () => {
    if (!editing) {
      onSelect();
    }
  };

  const handleSave = event => {
    event.stopPropagation();

    if (!editingValue.trim()) return;

    onSave();
  };

  const handleCancel = event => {
    event.stopPropagation();
    onCancel();
  };

  const handleEdit = event => {
    event.stopPropagation();
    onEdit();
  };

  const handleInputClick = event => {
    event.stopPropagation();
  };

  const handleInputKeyDown = event => {
    event.stopPropagation();

    if (event.key === 'Enter') {
      event.preventDefault();

      if (editingValue.trim()) {
        onSave();
      }
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <TableRow
      hover={!editing}
      selected={selected}
      onClick={handleRowClick}
      tabIndex={editing ? -1 : 0}
      role="button"
      aria-selected={selected}
      onKeyDown={event => {
        if (editing) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={[
        clickableTableRowSx,
        editing && editingTableRowSx,
        {
          '&:last-child td, &:last-child th': {
            borderBottom: 'none'
          }
        }
      ]}>
      <TableCell sx={addInputCellSx}>
        {editing ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={editingValue}
            onChange={event => onEditingValueChange(event.target.value)}
            onClick={handleInputClick}
            onKeyDown={handleInputKeyDown}
            sx={inputSx}
          />
        ) : (
          <Box component="span" title={topic.title} sx={clickableTopicTextSx}>
            {topic.title}
          </Box>
        )}
      </TableCell>

      <TableCell align="center" sx={addActionCellSx}>
        {editing ? (
          <Box sx={actionWrapperSx}>
            <IconButton
              size="small"
              aria-label={`Save ${topic.title}`}
              disabled={!editingValue.trim()}
              onClick={handleSave}
              disableRipple
              disableFocusRipple
              sx={{
                ...actionIconSx,
                ...addIconSx,
                ...iconButtonNoAnimationSx
              }}>
              <CheckOutlined sx={actionSvgSx} />
            </IconButton>

            <IconButton
              size="small"
              aria-label={`Cancel editing ${topic.title}`}
              onClick={handleCancel}
              sx={{
                ...actionIconSx,
                ...clearIconSx,
                ...iconButtonNoAnimationSx
              }}>
              <ClearOutlined sx={actionSvgSx} />
            </IconButton>
          </Box>
        ) : (
          <Box sx={actionWrapperSx}>
            <IconButton
              size="small"
              aria-label={`Edit ${topic.title}`}
              onClick={handleEdit}
              sx={{
                ...actionIconSx,
                ...editIconButtonSx
              }}>
              <EditOutlined sx={editSvgSx} />
            </IconButton>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

TemplateRow.propTypes = {
  topic: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired
  }).isRequired,
  selected: PropTypes.bool,
  editing: PropTypes.bool,
  editingValue: PropTypes.string,
  onEditingValueChange: PropTypes.func,
  onSelect: PropTypes.func,
  onEdit: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func
};

TemplateRow.defaultProps = {
  selected: false,
  editing: false,
  editingValue: '',
  onEditingValueChange: () => {},
  onSelect: () => {},
  onEdit: () => {},
  onSave: () => {},
  onCancel: () => {}
};

export default TemplateRow;
